import {
  useAsyncData,
  useNuxtApp,
  useRequestHeaders,
  useRuntimeConfig
} from '#app';
import { computed, unref } from 'vue';
import {
  cachedSiteSettings,
  normalizeSiteSettings,
  siteSettingKeys
} from '~/utils/site-settings';

// Only public campaign/popup settings. Keep personalized price/auth caches intact.
export function useSiteSettings(locale) {
  const config = useRuntimeConfig();
  const app = useNuxtApp();
  // App/request-scoped, bounded to supported locales; never shared between SSR users.
  // Layout handovers may abort Nuxt's AsyncData subscriber. Keep the small public
  // GET alive until its own timeout so the replacement layout can reuse it.
  const records = (app._idmanifyPublicSiteSettings ||= {
    cache: new Map(),
    pending: new Map()
  });
  const headers = import.meta.server ? useRequestHeaders(['cookie']) : {};
  const key = computed(() => `site-settings:public:v2:${unref(locale)}`);
  return useAsyncData(
    key,
    async () => {
      const language = unref(locale);
      if (records.pending.has(language)) return records.pending.get(language);
      const request = $fetch('/api/settings/site', {
        baseURL: config.public.API_URL,
        credentials: 'include',
        headers: {
          ...headers,
          accept: 'application/json',
          ...(import.meta.server ? { referer: config.public.FRONTEND_URL } : {})
        },
        query: { 'keys[]': siteSettingKeys, lng: language },
        timeout: 8000,
        retry: 0
      })
        .then((response) => {
          const entry = {
            locale: language,
            fetchedAt: Date.now(),
            settings: normalizeSiteSettings(response)
          };
          records.cache.set(language, entry);
          return entry;
        })
        .finally(() => {
          if (records.pending.get(language) === request)
            records.pending.delete(language);
        });
      records.pending.set(language, request);
      return request;
    },
    {
      // Reactive key is the single request trigger, not a second query watcher.
      watch: false,
      dedupe: 'defer',
      getCachedData(cacheKey, app, context) {
        const entry =
          app.payload?.data?.[cacheKey] ?? app.static?.data?.[cacheKey];
        // Reuse exact SSR payload during hydration, even if the document was cached.
        // Countdown/offer selection separately rejects expired campaigns.
        if (app.isHydrating && entry?.locale === unref(locale)) {
          records.cache.set(entry.locale, entry);
          return entry;
        }
        // Manual refresh must actually refetch; navigation may reuse a short cache.
        if (context?.cause && context.cause !== 'initial') return undefined;
        return (
          cachedSiteSettings(entry, unref(locale)) ||
          cachedSiteSettings(records.cache.get(unref(locale)), unref(locale))
        );
      }
    }
  );
}
